// SPDX-FileCopyrightText: 2026 Bundesrepublik Deutschland, vertreten durch das BMI/ITZBund
//
// SPDX-License-Identifier: GPL-3.0-or-later

/*
 * This file is part of the package itzbund/gsb-places of the GSB 11 Project by ITZBund
 *
 * Copyright (C) 2025 Bundesrepublik Deutschland, vertreten durch das
 * BMI/ITZBund. Author: Thomas Maroschik
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {LitElement, css} from "lit";
import {consume} from "@lit/context";
import {gsbMapContext} from "../contexts/gsb-map-context";
import {property} from "lit/decorators.js";
import {Map as MapLibreMap} from "maplibre-gl";
import configuration from "../deckgl/json-configuration";
import {DeckGLProps, View} from "deck.gl";
import {MapboxOverlay} from '@deck.gl/mapbox';
import {MapView, MapViewState} from "@deck.gl/core";
import {LightTheme, LoadingWidget} from '@deck.gl/widgets';
import {JSONConverter} from "@deck.gl/json";
import styles from './gsb-map-deckgl-control.css';
import chroma from 'chroma-js';

export type GsbMapViewState = MapViewState & {
  /** [[lon, lat], [lon, lat]] */
  bounds: [[number, number], [number, number]],
};

export class GsbMapDeckglControl extends LitElement {

  @consume({context: gsbMapContext, subscribe: true})
  @property({attribute: false})
  protected map?: MapLibreMap;

  @property({type: Object})
  public json?: object;

  @property({attribute: 'json-url'})
  public jsonUrl?: string;

  private overlay?: MapboxOverlay;

  static get is() {
    return 'gsb-map-control-deckgl';
  }

  static get styles() {
    return styles;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeMapListeners(this.map);
    this.destroyOverlay();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('map')) {
      this.handleMapChange(changedProperties.get('map'), this.map);
    }

    if (changedProperties.has('jsonUrl') && this.jsonUrl) {
      this.fetchJsonFromUrl();
    }

    if (!this.map || !this.json) {
      return;
    }

    if (changedProperties.has('json') || changedProperties.has('map')) {
      this.syncDeckglState();
    }
  }

  private handleMapChange(oldMap: MapLibreMap | undefined, newMap: MapLibreMap | undefined) {
    this.removeMapListeners(oldMap);
    if (newMap) {
      newMap.on('style.loading', this.onStyleLoading);
      newMap.on('style.load', this.onStyleLoad);
    }
  }

  private removeMapListeners(map?: MapLibreMap) {
    if (map) {
      map.off('style.loading', this.onStyleLoading);
      map.off('style.load', this.onStyleLoad);
    }
  }

  private destroyOverlay() {
    if (!this.overlay) return;

    if (this.map?.hasControl(this.overlay)) {
      this.map.removeControl(this.overlay);
    }
    this.overlay.finalize();
    this.overlay = undefined;
  }

  private syncDeckglState() {
    if (!this.map || !this.json) return;

    const {views, props, viewState} = this.parseJson(this.json);

    if (this.handleStyleChange(views)) {
      return; // Stop execution if style is changing; wait for onStyleLoad
    }

    if (this.overlay) {
      this.overlay.setProps(props);
      if (viewState) this.applyViewState(viewState);
    } else if (this.map.isStyleLoaded()) {
      this.onStyleLoad();
    }
  }

  private handleStyleChange(views: View | View[]): boolean {
    const viewArray = Array.isArray(views) ? views : [views];
    const mainView = viewArray.find(v => v instanceof MapView);
    const targetStyle = mainView?.props?.mapStyle;

    if (targetStyle && targetStyle !== this.map?.getStyleUrl()) {
      this.map?.setStyle(targetStyle);
      return true;
    }
    return false;
  }

  private async fetchJsonFromUrl() {
    if (!this.jsonUrl) return;
    try {
      const response = await fetch(this.jsonUrl);
      if (response.ok) {
        this.json = await response.json();
      } else {
        console.error(`Failed to fetch JSON from ${this.jsonUrl}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error fetching JSON from ${this.jsonUrl}`, error);
    }
  }

  private onStyleLoading = () => {
    this.destroyOverlay();
  }

  private onStyleLoad = () => {
    if (!this.map || this.overlay || !this.json) {
      return;
    }

    const {viewState, props} = this.parseJson(this.json);

    this.overlay = new MapboxOverlay({
      // @ts-ignore
      gsbMapDeckglControl: this,
      style: LightTheme,
      interleaved: true,
      getCursor: this.onGetCursor,
      pickingRadius: 16,
    });

    if (viewState) this.applyViewState(viewState);
    this.overlay.setProps(props);
    this.map.addControl(this.overlay);
  }

  private parseJson(json?: object): { viewState?: GsbMapViewState, views: View | View[], props: DeckGLProps } {
    // we always need a fresh instance of the converter, as keeps an internal copy of the json
    // and thus doesn't reparse the functions like "@@function": "getGlobalState"
    const jsonConverterConfig = {...configuration};

    jsonConverterConfig.functions = {
      ...jsonConverterConfig.functions,
      getGlobalState: this.getGlobalState
    };

    jsonConverterConfig.enumerations = {
      ...jsonConverterConfig.enumerations,
      ...this.gsbColors()
    };

    const jsonConverter = new JSONConverter({configuration: jsonConverterConfig});
    const props = (jsonConverter.convert(json) as DeckGLProps);
    props.widgets = props.widgets || [];
    props.widgets.push(new LoadingWidget({placement: 'top-left'}));

    return {
      viewState: props.initialViewState || props.viewState || null,
      views: props.views || null,
      props
    };
  }

  private gsbColors() {
    const styles = getComputedStyle(this);
    const gsb = {
      "primary": chroma(styles.getPropertyValue('--gsb-primary')).rgb(),
      "secondary": chroma(styles.getPropertyValue('--gsb-secondary')).rgb(),
      "tertiary": chroma(styles.getPropertyValue('--gsb-tertiary')).rgb(),
      "quaternary": chroma(styles.getPropertyValue('--gsb-quaternary')).rgb(),

      "body-color": chroma(styles.getPropertyValue('--gsb-body-color')).rgb(),
      "body-bg": chroma(styles.getPropertyValue('--gsb-body-bg')).rgb(),
      "emphasis-color": chroma(styles.getPropertyValue('--gsb-emphasis-color')).rgb(),
      "link-color": chroma(styles.getPropertyValue('--gsb-link-color')).rgb(),
      "border-color": chroma(styles.getPropertyValue('--gsb-border-color')).rgb(),
      "highlight-color": chroma(styles.getPropertyValue('--gsb-highlight-color')).rgb(),

      "bgcolor-1": chroma(styles.getPropertyValue('--gsb-bgcolor-1')).rgb(),
      "bgcolor-2": chroma(styles.getPropertyValue('--gsb-bgcolor-2')).rgb(),
      "bgcolor-3": chroma(styles.getPropertyValue('--gsb-bgcolor-3')).rgb(),
      "bgcolor-4": chroma(styles.getPropertyValue('--gsb-bgcolor-4')).rgb(),
      "bgcolor-5": chroma(styles.getPropertyValue('--gsb-bgcolor-5')).rgb(),
      "bgcolor-6": chroma(styles.getPropertyValue('--gsb-bgcolor-6')).rgb(),

      "success": chroma(styles.getPropertyValue('--gsb-success')).rgb(),
      "info": chroma(styles.getPropertyValue('--gsb-info')).rgb(),
      "warning": chroma(styles.getPropertyValue('--gsb-warning')).rgb(),
      "danger": chroma(styles.getPropertyValue('--gsb-danger')).rgb(),
      "light": chroma(styles.getPropertyValue('--gsb-light')).rgb(),
      "dark": chroma(styles.getPropertyValue('--gsb-dark')).rgb(),

    };
    for (let i = 2; i <= 8; i++) {
      gsb["palette-primary-" + i] = chroma.bezier([gsb["body-bg"], gsb.primary]).scale().colors(i, 'rgb');
      gsb["palette-secondary-" + i] = chroma.bezier([gsb["body-bg"], gsb.secondary]).scale().colors(i, 'rgb');
      gsb["palette-tertiary-" + i] = chroma.bezier([gsb["body-bg"], gsb.tertiary]).scale().colors(i, 'rgb');
      gsb["palette-quaternary-" + i] = chroma.bezier([gsb["body-bg"], gsb.quaternary]).scale().colors(i, 'rgb');
    }
    return { gsb };
  }

  private applyViewState(viewState: GsbMapViewState) {
    if (!this.map) return;

    if (viewState.bounds) {
      this.map.fitBounds(viewState.bounds, {
        maxZoom: viewState.maxZoom || 22,
        pitch: viewState.pitch || 0,
        bearing: viewState.bearing || 0,
        padding: 16
      });
    } else {
      this.map.jumpTo({
        center: [viewState.longitude || 0, viewState.latitude || 0],
        zoom: viewState.zoom || 0,
        pitch: viewState.pitch || 0,
        bearing: viewState.bearing || 0
      });
    }
  }

  private onGetCursor = ({isHovering, isDragging}: { isHovering: boolean, isDragging: boolean }): string => {
    if (isHovering) return 'pointer';
    if (isDragging) return 'grabbing';
    return 'grab';
  }

  private getGlobalState = ({propertyName}: { propertyName: string }): any => {
    if (!this.map || !this.map.style) {
      return undefined;
    }
    const globalState = this.map.getGlobalState();
    return propertyName ? globalState[propertyName] : globalState;
  }
}

customElements.define(GsbMapDeckglControl.is, GsbMapDeckglControl);
